import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getCompare } from "@/lib/procureApi";
import { fmtDate, fmtInt } from "@/lib/format";
import { ArrowRight, ArrowUp, ArrowDown, Minus, GitDiff } from "@phosphor-icons/react";

const DeltaPill = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-[#9CA3AF]">—</span>;
  if (value === 0)
    return (
      <span className="inline-flex items-center gap-1 font-mono text-[#64748B]">
        <Minus size={12} weight="bold" /> 0d
      </span>
    );
  const slipped = value > 0; // slower = worse
  const Icon = slipped ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-1 font-mono font-semibold ${slipped ? "text-red-600" : "text-emerald-600"}`}>
      <Icon size={12} weight="bold" />
      {slipped ? "+" : ""}{value}d
    </span>
  );
};

const SnapSelect = ({ label, value, onChange, snapshots, testid }) => (
  <div className="flex flex-col gap-1">
    <span className="pf-label">{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9 w-44 rounded-sm border-[#E3E7EC] bg-white text-sm text-[#0F172A] focus:ring-[#F0323E]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-sm border-[#E3E7EC] bg-white text-[#0F172A]">
        {snapshots.map((s) => (
          <SelectItem key={s.id} value={s.id} className="text-sm">{fmtDate(s.reference_date)}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const StatBox = ({ label, base, target, unit = "" }) => {
  const delta = base != null && target != null ? Math.round((target - base) * 10) / 10 : null;
  return (
    <div className="border border-[#E3E7EC] bg-white p-3">
      <div className="pf-label">{label}</div>
      <div className="mt-1 flex items-baseline gap-2 font-mono">
        <span className="text-[#9CA3AF]">{fmtInt(base)}{unit}</span>
        <ArrowRight size={12} className="text-[#CAD1DB]" />
        <span className="text-lg font-semibold text-[#0F172A]">{fmtInt(target)}{unit}</span>
      </div>
      <div className="mt-0.5 text-[11px]"><DeltaPill value={delta} /></div>
    </div>
  );
};

export const SnapshotCompare = ({ open, onClose, snapshots }) => {
  const enough = snapshots.length >= 2;
  const [baseId, setBaseId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !enough) return;
    setTargetId((t) => t || snapshots[0].id);
    setBaseId((b) => b || snapshots[1].id);
  }, [open, enough, snapshots]);

  useEffect(() => {
    if (!open || !enough || !baseId || !targetId || baseId === targetId) {
      if (baseId && targetId && baseId === targetId) setData(null);
      return;
    }
    setLoading(true);
    getCompare(baseId, targetId)
      .then(setData)
      .finally(() => setLoading(false));
  }, [open, enough, baseId, targetId]);

  const improved = useMemo(() => (data?.buyers || []).filter((r) => r.delta != null && r.delta < 0).length, [data]);
  const slipped = useMemo(() => (data?.buyers || []).filter((r) => r.delta != null && r.delta > 0).length, [data]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="compare-dialog"
        className="max-h-[88vh] max-w-4xl overflow-hidden border-[#E3E7EC] bg-[#F4F5F7] p-0 text-[#0F172A]"
      >
        <DialogHeader className="border-b border-[#E3E7EC] bg-white px-6 py-4 text-left">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <GitDiff size={20} weight="duotone" className="text-[#F0323E]" /> Snapshot Compare
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            See which buyers improved or slipped on turnaround between two uploads.
          </DialogDescription>
        </DialogHeader>

        {!enough ? (
          <div className="px-6 py-16 text-center text-sm text-[#64748B]">
            Only one snapshot loaded. Upload the report on another day to compare turnaround side-by-side.
          </div>
        ) : (
          <div className="overflow-y-auto px-6 py-5" style={{ maxHeight: "calc(88vh - 90px)" }}>
            <div className="flex flex-wrap items-end gap-4">
              <SnapSelect label="Base (from)" value={baseId} onChange={setBaseId} snapshots={snapshots} testid="compare-base" />
              <ArrowRight size={18} className="mb-2 text-[#CAD1DB]" />
              <SnapSelect label="Target (to)" value={targetId} onChange={setTargetId} snapshots={snapshots} testid="compare-target" />
              {data && (
                <div className="mb-1 ml-auto flex gap-4 font-mono text-xs">
                  <span className="text-emerald-600">▼ {improved} improved</span>
                  <span className="text-red-600">▲ {slipped} slipped</span>
                </div>
              )}
            </div>

            {baseId === targetId ? (
              <p className="mt-8 text-center text-sm text-[#9CA3AF]">Pick two different dates to compare.</p>
            ) : loading || !data ? (
              <p className="mt-8 text-center text-sm text-[#64748B]">Loading comparison…</p>
            ) : (
              <>
                <div className="mt-5 grid grid-cols-3 gap-px bg-[#E3E7EC]">
                  <StatBox label="Avg Turnaround" base={data.base.summary.avg_turnaround} target={data.target.summary.avg_turnaround} unit="d" />
                  <StatBox label="Open Lines" base={data.base.summary.lines} target={data.target.summary.lines} />
                  <StatBox label="Aging > 30d" base={data.base.summary.stuck} target={data.target.summary.stuck} />
                </div>

                <div className="mt-6 border border-[#E3E7EC] bg-white">
                  <table className="w-full border-collapse text-sm" data-testid="compare-table">
                    <thead>
                      <tr className="border-b border-[#E3E7EC] text-left">
                        {["Buyer", `${fmtDate(data.base.reference_date)}`, `${fmtDate(data.target.reference_date)}`, "Δ Turnaround", "Lines"].map((h) => (
                          <th key={h} className="whitespace-nowrap px-4 py-2.5 pf-label">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.buyers.map((r, i) => (
                        <tr key={r.buyer} data-testid={`compare-row-${i}`} className="border-b border-[#E3E7EC]/60 hover:bg-[#EDEFF3]">
                          <td className="px-4 py-2 text-[#0F172A]">
                            {r.buyer}
                            {r.status === "new" && <span className="ml-2 rounded-sm bg-[#F0323E]/10 px-1.5 py-0.5 font-mono text-[10px] text-[#F0323E]">new</span>}
                            {r.status === "gone" && <span className="ml-2 rounded-sm bg-[#EDEFF3] px-1.5 py-0.5 font-mono text-[10px] text-[#9CA3AF]">gone</span>}
                          </td>
                          <td className="px-4 py-2 font-mono text-[#64748B]">{r.base_avg != null ? `${r.base_avg}d` : "—"}</td>
                          <td className="px-4 py-2 font-mono text-[#0F172A]">{r.target_avg != null ? `${r.target_avg}d` : "—"}</td>
                          <td className="px-4 py-2"><DeltaPill value={r.delta} /></td>
                          <td className="px-4 py-2 font-mono text-[#64748B]">{fmtInt(r.base_count)} → {fmtInt(r.target_count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
